import React, { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.42-.22-2.04H12v4.1h6.52c-.13.98-.85 2.45-2.45 3.44l3.57 2.77c2.08-1.92 3.88-4.9 3.88-8.27z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.57-2.77c-.98.68-2.3 1.17-4.37 1.17-3.34 0-6.17-2.25-7.18-5.28L.76 17c1.95 3.98 6.04 7 11.24 7z" />
    <path fill="#FBBC05" d="M4.82 14.2c-.24-.68-.37-1.4-.37-2.2s.13-1.52.37-2.2L.76 6.6A11.96 11.96 0 0 0 0 12c0 1.92.46 3.74 1.24 5.4z" style={{ transform: "translateY(0)" }} />
    <path fill="#EA4335" d="M12 4.75c2.28 0 3.82.98 4.7 1.8l3.18-3.1C17.95 1.52 15.24.24 12 .24 6.8.24 2.71 3.26.76 7.24l4.46 3.16c1.01-3.03 3.84-5.65 6.78-5.65z" />
  </svg>
);

const MicrosoftIcon = () => (
  <svg viewBox="0 0 23 23" className="w-4 h-4" aria-hidden="true">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#7FBA00" d="M12 1h10v10H12z" />
    <path fill="#00A4EF" d="M1 12h10v10H1z" />
    <path fill="#FFB900" d="M12 12h10v10H12z" />
  </svg>
);

/**
 * Shared SSO buttons — used on the Login page and in Settings > Connected
 * Accounts (where `intent="link"` relinks an already-signed-in user).
 * Google is fully wired to Emergent-managed Auth. Microsoft is a wireframe:
 * no live Azure AD app registration exists yet, so it shows what the flow
 * will look like without actually authenticating anyone.
 */
export default function SsoButtons({ intent = "login", className = "" }) {
  const [msOpen, setMsOpen] = useState(false);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const callbackUrl = `${window.location.origin}/auth/google/callback${intent === "link" ? "?intent=link" : ""}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(callbackUrl)}`;
  };

  return (
    <>
      <div className="flex items-center gap-2 my-5" data-testid="sso-divider">
        <div className="h-px flex-1 bg-border" />
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {intent === "link" ? "Link an account" : "Or continue with"}
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" className="gap-2 h-10" onClick={startGoogle} data-testid="sso-google-button">
          <GoogleIcon /> Google
        </Button>
        <Button type="button" variant="outline" className="gap-2 h-10" onClick={() => setMsOpen(true)} data-testid="sso-microsoft-button">
          <MicrosoftIcon /> Microsoft
        </Button>
      </div>

      <Dialog open={msOpen} onOpenChange={setMsOpen}>
        <DialogContent className="max-w-sm" data-testid="sso-microsoft-wireframe-dialog">
          <DialogHeader>
            <DialogTitle>Microsoft sign-in — coming soon</DialogTitle>
            <DialogDescription>
              This is a preview of the Microsoft / Entra ID sign-in flow. It needs an Azure AD app
              registration (client ID + secret) from your IT admin before it can go live — once that's
              set up, this button will work exactly like the Google one.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsOpen(false)} data-testid="sso-microsoft-wireframe-close">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
